import React, { useEffect, useState, useRef } from 'react';
import {
  IoLogOutOutline,
  IoClose,
  IoMenu,
  IoPencil,
  IoCheckmark,
  IoTrashOutline,
  IoShareSocialOutline,
} from 'react-icons/io5';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

import { UserAvatar } from './UserAvatar';
import { API_URL } from '../services/api';
import { clearLocalStorageKeepingLoginHint } from '../config/authStorageKeys';
import { DataState } from '../context/DataProvider';
import ProfileSettingsModal from './ProfileSettingsModal';
import ShareChatModal from './chat/ShareChatModal';
import { useAccess } from '../services/access';
import { ANALYTICS_TABS } from '../utils/analyticsTabs';
import { taskTitle } from '../utils/taskTitle';

const Sidebar = ({ toggleSidebar, isOpen, onToggle, tabName }) => {
  const {
    tasks,
    setTasks,
    currentTaskId,
    activateTask,
    handleCreateTask,
    setJustChangedLocalStorageData,
    userInfo,
    handleDeleteTask,
    userGoogleId,
  } = DataState();

  const [openSettings, setOpenSettings] = useState(false);
  const sidebarRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { can } = useAccess();
  const isAnalytics = tabName === 'analytics';

  useEffect(() => {
    const closeSettings = () => setOpenSettings(false);
    window.addEventListener('oqlous:close-all-modals', closeSettings);
    return () =>
      window.removeEventListener('oqlous:close-all-modals', closeSettings);
  }, []);

  // --- STATE FOR EDITING ---
  const [editingTaskId, setEditingTaskId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const editInputRef = useRef(null);

  // --- STATE FOR DELETE CONFIRMATION ---
  const [deletingTaskId, setDeletingTaskId] = useState(null);
  const [sharingTask, setSharingTask] = useState(null);

  // Focus input when editing starts
  useEffect(() => {
    if (editingTaskId && editInputRef.current) {
      editInputRef.current.focus();
    }
  }, [editingTaskId]);

  const handleLogout = () => {
    async function logout() {
      try {
        await fetch(`${API_URL}/logout`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
        });
      } catch (error) {
        toast.error('Logout failed. Please try again.');
      } finally {
        clearLocalStorageKeepingLoginHint();
        setJustChangedLocalStorageData(true);
        navigate('/login');
      }
    }
    logout();
  };

  useEffect(() => {
    const storedUserGoogleId = localStorage.getItem('projectToken');
    if (!storedUserGoogleId) {
      navigate('/login');
    }
  }, [navigate]);

  const handleTaskSelect = (task) => {
    // activateTask (DataProvider) is the single shared implementation of
    // "make this task the active chat" -- also used by Ui.js's URL-sync
    // effect, so both triggers stay in lockstep instead of risking drift.
    activateTask(task);

    // Update URL to reflect the selected chat
    navigate(`/chat/${task._id}`);

    if (window.innerWidth < 768 && onToggle) {
      onToggle();
    }
  };

  // --- NEW: HANDLE RENAME ---
  const startEditing = (e, task) => {
    e.stopPropagation(); // Prevent selecting the task
    setEditingTaskId(task._id);
    setEditValue(taskTitle(task));
  };

  const cancelEditing = (e) => {
    if (e) e.stopPropagation();
    setEditingTaskId(null);
    setEditValue('');
  };

  const saveTaskName = async (e, taskId) => {
    if (e) e.stopPropagation();

    if (
      !editValue.trim() ||
      editValue === taskTitle(tasks.find((t) => t._id === taskId))
    ) {
      cancelEditing();
      return;
    }

    const token = localStorage.getItem('projectToken');

    try {
      const response = await fetch(`${API_URL}/tasks/${taskId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ title: editValue }),
      });

      const data = await response.json();

      if (response.ok) {
        // Update local state immediately to reflect change
        if (setTasks) {
          setTasks((prevTasks) => {
            const taskIndex = prevTasks.findIndex((t) => t._id === taskId);
            if (taskIndex === -1) return prevTasks;

            const updatedTask = {
              ...prevTasks[taskIndex],
              title: data.task?.title || editValue,
            };

            const otherTasks = prevTasks.filter((_, idx) => idx !== taskIndex);
            return [updatedTask, ...otherTasks];
          });
        }
        toast.success('Agent renamed');
      } else {
        toast.error(data.error || 'Failed to rename');
      }
    } catch (error) {
      toast.error('Network error occurred');
    } finally {
      setEditingTaskId(null);
    }
  };

  const handleKeyDown = (e, taskId) => {
    if (e.key === 'Enter') {
      saveTaskName(e, taskId);
    } else if (e.key === 'Escape') {
      cancelEditing(e);
    }
  };

  // --- DELETE HANDLERS ---
  const confirmDelete = async (e, taskId) => {
    e.stopPropagation();
    await handleDeleteTask(taskId);
    setDeletingTaskId(null);
    toast.success('Chat deleted');
  };

  const cancelDelete = (e) => {
    if (e) e.stopPropagation();
    setDeletingTaskId(null);
  };

  // Close sidebar logic...
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isOpen && window.innerWidth < 768) {
        const sidebar = sidebarRef.current;
        const menuButton = document.querySelector('[aria-label="Toggle menu"]');
        if (
          sidebar &&
          !sidebar.contains(event.target) &&
          menuButton &&
          !menuButton.contains(event.target) &&
          !event.target.closest('.modal, [role="dialog"]') &&
          !openSettings
        ) {
          onToggle && onToggle();
        }
      }
    };

    if (isOpen) {
      // Add delay to prevent immediate closure
      const timeoutId = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
      }, 100);

      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen, onToggle, openSettings]);

  // Prevent body scroll when mobile sidebar is open
  useEffect(() => {
    if (isOpen && window.innerWidth < 768) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const sidebarContent = (
    <div className="bg-1 flex h-full flex-col justify-between gap-1 overflow-hidden rounded-none md:rounded-2xl">
      {/* Header Section */}
      <div className="relative flex flex-col items-center gap-3 px-3 py-4 md:px-2">
        {/* Mobile Header with Close Button */}
        <div className="mb-2 flex w-full items-center justify-between md:hidden">
          <button
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              onToggle && onToggle();
            }}
            className="absolute right-1 top-1 z-50 rounded-lg p-2 transition-colors hover:bg-white hover:bg-opacity-10"
          >
            <IoClose className="text-xl" />
          </button>
        </div>

        {/* Logo Container */}
        <div className="mb-1 flex w-full items-center justify-center gap-3 md:mb-4">
          {/* Main Logo (Always visible) */}
          <img
            src="/assets/logo.png"
            alt="Main Logo"
            className={`${toggleSidebar ? 'h-8 md:h-10' : 'h-10 md:h-12'} transform object-contain transition-all duration-100`}
          />

          {/* Partner Logo (Conditionally renders if it exists) */}
          {userInfo?.partnerLogo && !toggleSidebar && (
            <>
              {/* Visual Separator for clarity - RESTORED COMMENT */}
              <div
                className="h-6 border-l border-white/30"
                aria-hidden="true"
              ></div>

              <img
                src={`/assets/partners/${userInfo.partnerLogo}`}
                alt="Partner Logo"
                className="h-7 object-contain md:h-9"
              />
            </>
          )}
        </div>
        {/* <img
          src={theme === "light" ? "/assets/logo.png" : "/assets/logo.png"}
          alt="Logo"
          className="w-full h-8 mb-2 text-center md:mb-4 md:h-10"
        /> */}

        {/* Action Buttons */}
        <div className="flex w-full flex-col gap-3 md:gap-4">
          {can('chat:send') && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                handleCreateTask();
                navigate('/chat');
                if (window.innerWidth < 768 && onToggle) {
                  setTimeout(() => onToggle(), 100);
                }
              }}
              className={`flex cursor-pointer items-center justify-center 
              gap-2 rounded-xl bg-primary-gradient px-3 py-3 text-sm 
              text-white transition-all duration-200 hover:opacity-90 sm:text-sm md:py-2 md:text-xs 
              ${toggleSidebar && 'text-xl font-bold'}`}
            >
              {toggleSidebar ? '+' : 'New Chat'}
            </button>
          )}

          {!toggleSidebar && !isAnalytics && (
            <button
              className="flex items-center justify-between gap-1 px-2 text-sm font-semibold transition-colors hover:text-opacity-80 md:text-xs"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
              }}
            >
              Recent Agents
            </button>
          )}
        </div>
      </div>

      {/* Analytics section nav: replaces recent-chats history while on
          Analytics, since a chat list has no meaning there. Divided from
          New Chat with a rule so it doesn't read as more chat history, and
          (unlike the history list) stays visible — icon-only — when the
          sidebar is collapsed, since these are primary page navigation. */}
      {isAnalytics && (
        <>
          <div className="mx-3 border-t border-white border-opacity-10 md:mx-2" />
          <div className="flex flex-1 flex-col gap-1 overflow-y-auto px-2 py-2">
            {ANALYTICS_TABS.filter(
              (tab) => !tab.permission || can(tab.permission),
            ).map((tab) => {
              const isActive = location.pathname === tab.path;
              const Icon = tab.icon;
              return (
                <Link
                  key={tab.id}
                  to={tab.path}
                  onClick={() => {
                    if (window.innerWidth < 768 && onToggle) {
                      setTimeout(() => onToggle(), 100);
                    }
                  }}
                  className={`flex items-center gap-3 rounded-lg p-3 transition-all duration-200 md:p-2 ${
                    toggleSidebar ? 'justify-center' : 'justify-start'
                  } ${
                    isActive
                      ? 'bg-primary-gradient text-white'
                      : 'text-zinc-300 hover:bg-white hover:bg-opacity-10'
                  }`}
                >
                  <Icon className="h-4 w-4 flex-shrink-0" />
                  {!toggleSidebar && (
                    <span className="text-sm md:text-xs">{tab.label}</span>
                  )}
                </Link>
              );
            })}
          </div>
        </>
      )}

      {/* History Section */}
      {!toggleSidebar && !isAnalytics && (
        <div className="hide-sidebar bg-2 mx-2 -mt-3 flex flex-1 flex-col gap-1 overflow-y-auto rounded-xl p-3 md:mx-2">
          {/* Changed gap-2 to gap-1 above for tighter list */}
          {tasks?.length > 0 ? (
            tasks.map((task) => (
              // eslint-disable-next-line
              <div
                key={task._id}
                role="button"
                tabIndex={0}
                className="group relative"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  if (editingTaskId === task._id) return;
                  handleTaskSelect(task);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    if (editingTaskId === task._id) return;
                    handleTaskSelect(task);
                  }
                }}
              >
                {/* eslint-disable-next-line */}
                <div
                  className={`${
                    task._id === currentTaskId
                      ? 'bg-white bg-opacity-15'
                      : 'bg-white bg-opacity-5'
                  } flex min-h-[30px] cursor-pointer items-center justify-between gap-2 rounded-lg px-3 py-1 transition-all duration-200
                    hover:bg-zinc-800 md:py-1`}
                >
                  {/* --- EDIT MODE --- */}
                  {editingTaskId === task._id ? (
                    <div className="flex w-full items-center gap-2">
                      <input
                        ref={editInputRef}
                        type="text"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onKeyDown={(e) => handleKeyDown(e, task._id)}
                        onClick={(e) => e.stopPropagation()}
                        className="w-full border-b border-[#5652CF] bg-transparent text-sm text-white outline-none focus:border-blue-400 sm:text-sm md:text-xs"
                      />
                      <div className="flex items-center gap-1">
                        <button
                          onClick={(e) => saveTaskName(e, task._id)}
                          className="p-1 text-green-400 hover:text-green-300"
                        >
                          <IoCheckmark />
                        </button>
                        <button
                          onClick={(e) => cancelEditing(e)}
                          className="p-1 text-red-400 hover:text-red-300"
                        >
                          <IoClose />
                        </button>
                      </div>
                    </div>
                  ) : (
                    /* --- VIEW MODE --- */
                    <>
                      <p
                        title={taskTitle(task)}
                        className="flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-sm sm:text-sm md:text-xs"
                      >
                        {taskTitle(task)}
                      </p>
                      {task.read_only && (
                        <span
                          title="Shared with you: read only"
                          className="shrink-0 rounded bg-amber-500/15 px-1 text-[10px] text-amber-300"
                        >
                          shared
                        </span>
                      )}

                      {/* Edit Icon - Hidden by default, visible on group-hover */}
                      {!editingTaskId && !task.read_only && (
                        <div className="absolute right-0 flex items-center gap-1 bg-zinc-800 px-1 opacity-0 transition-opacity group-hover:opacity-100">
                          <button
                            onClick={(e) => startEditing(e, task)}
                            className="p-1 text-gray-400 hover:text-amber-400"
                            title="Rename"
                          >
                            <IoPencil className="text-sm" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSharingTask(task);
                            }}
                            className="p-1 text-gray-400 hover:text-indigo-400"
                            title="Share or transfer"
                          >
                            <IoShareSocialOutline className="text-sm" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeletingTaskId(task._id);
                            }}
                            className="p-1 text-gray-400 hover:text-red-400"
                            title="Delete"
                          >
                            <IoTrashOutline className="text-sm" />
                          </button>
                        </div>
                      )}

                      {/* Delete confirmation */}
                      {deletingTaskId === task._id && (
                        <div className="absolute inset-0 z-10 flex items-center justify-between rounded-lg bg-red-900 bg-opacity-90 px-3">
                          <span className="text-xs text-white">Delete?</span>
                          <div className="flex items-center gap-1">
                            <button
                              onClick={(e) => confirmDelete(e, task._id)}
                              className="p-1 text-green-400 hover:text-green-300"
                            >
                              <IoCheckmark />
                            </button>
                            <button
                              onClick={(e) => cancelDelete(e)}
                              className="p-1 text-red-400 hover:text-red-300"
                            >
                              <IoClose />
                            </button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            ))
          ) : (
            <div className="flex flex-col gap-3 p-2 text-center text-sm">
              No recent chats
            </div>
          )}
        </div>
      )}

      {/* Bottom Actions */}
      <div className="flex w-full flex-col gap-1 border-t border-white border-opacity-10 px-3 py-3 text-sm md:border-none md:px-2">
        {/* +++ Make the User Info section a button to open the modal +++ - RESTORED COMMENT */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            setOpenSettings(true);
            // Close sidebar on mobile
            if (window.innerWidth < 768 && onToggle) {
              setTimeout(() => onToggle(), 100);
            }
          }}
          className={`flex w-full gap-3 rounded-lg ${
            toggleSidebar ? 'justify-center' : 'justify-start'
          } items-center p-3 transition-all duration-200 hover:bg-white hover:bg-opacity-10 md:p-2`}
        >
          {userInfo && (
            <div className="flex items-center gap-3">
              <UserAvatar user={userInfo} className="h-[25px] w-[25px]" />
              <span
                className={`max-w-20 overflow-hidden text-ellipsis whitespace-nowrap text-sm md:max-w-24 ${
                  toggleSidebar && 'hidden'
                }`}
              >
                {userInfo.name || userInfo.email}
              </span>
            </div>
          )}
        </button>

        <button
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            handleLogout();
          }}
          className={`flex gap-3 rounded-lg ${
            toggleSidebar ? 'justify-center' : 'justify-start'
          } items-center p-3 transition-all duration-200 hover:bg-white hover:bg-opacity-10 md:p-2`}
        >
          <IoLogOutOutline className="flex-shrink-0 text-xl md:text-2xl" />
          {!toggleSidebar && (
            <span className="text-sm md:text-sm">Log out</span>
          )}
        </button>
      </div>

      {/* Settings Modal */}
      {sharingTask && (
        <ShareChatModal
          task={sharingTask}
          accessToken={userGoogleId}
          onClose={() => setSharingTask(null)}
        />
      )}
      <ProfileSettingsModal
        isOpen={openSettings}
        onClose={() => setOpenSettings(false)}
        userInfo={userInfo}
      />
    </div>
  );

  return (
    <>
      {/* Desktop Sidebar */}
      <div className="hidden h-full md:block">{sidebarContent}</div>

      {/* Mobile Sidebar Overlay */}
      {isOpen && (
        <div className="fixed inset-0 z-50 flex md:hidden">
          {/* Backdrop */}
          {/* eslint-disable-next-line */}
          <div
            role="presentation"
            className="absolute inset-0 bg-black bg-opacity-50 transition-opacity"
            onClick={(e) => {
              e.stopPropagation();
              onToggle();
            }}
          />

          {/* Sidebar - Increased width and added ref */}
          {/* eslint-disable-next-line */}
          <div
            ref={sidebarRef}
            id="mobile-sidebar"
            role="presentation"
            className={`relative h-full w-80 max-w-[70vw] transform bg-gray-900 transition-transform duration-300 ease-in-out ${
              isOpen ? 'translate-x-0' : '-translate-x-full'
            }`}
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            {sidebarContent}
          </div>
        </div>
      )}

      {/* Mobile Menu Button - Only show when sidebar is closed */}
      {!isOpen && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            onToggle();
          }}
          className="fixed left-4 top-5 z-40 rounded-lg bg-opacity-50 p-2 backdrop-blur-sm transition-all duration-200 hover:bg-opacity-70 md:hidden"
          aria-label="Toggle menu"
        >
          <IoMenu className="text-2xl text-white" />
        </button>
      )}
    </>
  );
};

export default Sidebar;
